/**
 * CampusesCard — multi-campus management inside a school dashboard.
 *
 * Lets a school admin manage N campuses (physical/byod/online/satellite), with
 * per-campus toggles for:
 *   - share_timetable (same timetable across all campuses, or campus-owned)
 *   - share_academic_year (same year/terms, or campus-owned)
 *
 * Also ships a "Broadcast" button that opens a dialog to message one or many
 * campuses simultaneously across platform/email/sms/whatsapp channels.
 */
import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Plus, Building2, Globe, Laptop, Layers, Send, Loader2, Edit, Trash2, Users as UsersIcon, X } from 'lucide-react';
import { toast } from 'sonner';
import { authHeaders } from '@/lib/authToken';
import { useAuthStore } from '@/lib/store';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const hdrs = () => ({ ...authHeaders(), 'Content-Type': 'application/json' });

const TYPE_META = {
  physical: { icon: Building2, label: 'Physical', color: 'text-indigo-600' },
  byod:     { icon: Laptop,    label: 'BYOD',     color: 'text-fuchsia-600' },
  online:   { icon: Globe,     label: 'Online',   color: 'text-emerald-600' },
  satellite:{ icon: Layers,    label: 'Satellite',color: 'text-amber-600' },
};

const emptyForm = {
  name: '', code: '', type: 'physical',
  address: '', city: '', country: '', timezone: 'UTC',
  share_timetable: true, share_academic_year: true, is_active: true,
};

const CampusesCard = ({ tenantId }) => {
  const qc = useQueryClient();
  const { user } = useAuthStore();
  const isSuperAdmin = !!user && (user.is_super_admin === true || user.role === 'super_admin'
    || (Array.isArray(user.permissions) && (user.permissions.includes('*') || user.permissions.includes('super_admin'))));
  const [dialog, setDialog] = useState(null); // 'new' | campus object
  const [form, setForm] = useState(emptyForm);
  const [leadershipCampus, setLeadershipCampus] = useState(null);
  const [broadcast, setBroadcast] = useState(null); // null | {open}
  const [bc, setBc] = useState({
    subject: '', body: '',
    channels: { platform: true, email: false, sms: false, whatsapp: false },
    roles:    { student: true, teacher: true, staff: false, parent: false },
    target_campus_ids: [],  // [] = whole tenant
  });

  const { data, isLoading } = useQuery({
    queryKey: ['campuses', tenantId],
    queryFn: async () => {
      const r = await fetch(`${API_URL}/api/school/tenants/${tenantId}/campuses`, { headers: authHeaders() });
      if (!r.ok) throw new Error('Failed to load campuses');
      return r.json();
    },
    enabled: !!tenantId,
  });
  const campuses = data?.items || [];

  const save = useMutation({
    mutationFn: async () => {
      const isNew = dialog === 'new';
      const url = isNew
        ? `${API_URL}/api/school/tenants/${tenantId}/campuses`
        : `${API_URL}/api/school/tenants/${tenantId}/campuses/${dialog.id}`;
      const method = isNew ? 'POST' : 'PUT';
      const r = await fetch(url, { method, headers: hdrs(), body: JSON.stringify(form) });
      if (!r.ok) {
        const e = await r.json().catch(() => ({}));
        throw new Error(e.detail || 'Save failed');
      }
      return r.json();
    },
    onSuccess: () => {
      toast.success('Campus saved');
      qc.invalidateQueries({ queryKey: ['campuses', tenantId] });
      setDialog(null);
    },
    onError: (e) => toast.error(e.message),
  });

  const doBroadcast = useMutation({
    mutationFn: async () => {
      const payload = {
        subject: bc.subject,
        body: bc.body,
        channels: Object.keys(bc.channels).filter(k => bc.channels[k]),
        target_roles: Object.keys(bc.roles).filter(k => bc.roles[k]),
        target_campus_ids: bc.target_campus_ids,
      };
      if (!payload.subject || !payload.body) throw new Error('Subject and body are required');
      if (payload.channels.length === 0) throw new Error('Pick at least one channel');
      const r = await fetch(`${API_URL}/api/school/tenants/${tenantId}/campuses/broadcast`, {
        method: 'POST', headers: hdrs(), body: JSON.stringify(payload),
      });
      if (!r.ok) {
        const e = await r.json().catch(() => ({}));
        throw new Error(e.detail || 'Broadcast failed');
      }
      return r.json();
    },
    onSuccess: (res) => {
      toast.success(`Broadcast queued · ${res.recipient_count} recipient(s)`);
      setBroadcast(null);
      setBc({ subject: '', body: '',
        channels: { platform: true, email: false, sms: false, whatsapp: false },
        roles: { student: true, teacher: true, staff: false, parent: false },
        target_campus_ids: [],
      });
    },
    onError: (e) => toast.error(e.message),
  });

  const openNew = () => { setForm(emptyForm); setDialog('new'); };
  const openEdit = (c) => { setForm({ ...emptyForm, ...c }); setDialog(c); };

  return (
    <Card data-testid="campuses-card" className="border-slate-200">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between flex-wrap gap-2">
          <div>
            <CardTitle className="text-base font-semibold flex items-center gap-2">
              <Building2 className="h-4 w-4 text-indigo-600" /> Campuses
            </CardTitle>
            <CardDescription className="text-xs mt-0.5">
              Manage multiple campuses under one school. Campuses can share or own their own timetable/academic year.
            </CardDescription>
          </div>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={() => setBroadcast({ open: true })} data-testid="campus-broadcast-btn">
              <Send className="h-3.5 w-3.5 mr-1" /> Broadcast
            </Button>
            <Button size="sm" onClick={openNew} data-testid="new-campus-btn">
              <Plus className="h-3.5 w-3.5 mr-1" /> New campus
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="flex items-center justify-center py-6"><Loader2 className="h-4 w-4 animate-spin text-slate-400" /></div>
        ) : campuses.length === 0 ? (
          <p className="text-xs text-slate-500 py-4 text-center">No campuses yet. Click <strong>New campus</strong> to add your first physical, BYOD, or online campus.</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {campuses.map(c => {
              const meta = TYPE_META[c.type] || TYPE_META.physical;
              const Icon = meta.icon;
              return (
                <div key={c.id} className={`border rounded-lg p-3 bg-white ${!c.is_active ? 'opacity-60' : ''}`} data-testid={`campus-tile-${c.id}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <Icon className={`h-4 w-4 ${meta.color}`} />
                        <span className="font-semibold text-sm truncate">{c.name}</span>
                      </div>
                      <div className="text-[10px] text-slate-500 mt-0.5">
                        {c.code || meta.label} {c.city ? `· ${c.city}` : ''}
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      <Button size="sm" variant="ghost" className="h-7 w-7 p-0" title="Leadership" onClick={() => setLeadershipCampus(c)} data-testid={`campus-leadership-${c.id}`}>
                        <UsersIcon className="h-3.5 w-3.5" />
                      </Button>
                      <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => openEdit(c)} data-testid={`campus-edit-${c.id}`}>
                        <Edit className="h-3.5 w-3.5" />
                      </Button>
                      {isSuperAdmin && (
                        <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-rose-600 hover:bg-rose-50" title="Delete (super admin)"
                          onClick={() => {
                            if (!window.confirm(`Permanently delete campus "${c.name}"? Students/teachers/classes assigned will revert to tenant-wide.`)) return;
                            fetch(`${API_URL}/api/school/tenants/${tenantId}/campuses/${c.id}?hard=true`, { method: 'DELETE', headers: authHeaders() })
                              .then(r => r.json().then(j => ({ ok: r.ok, j })))
                              .then(({ ok, j }) => {
                                if (!ok) return toast.error(j.detail || 'Failed');
                                toast.success('Campus deleted');
                                qc.invalidateQueries({ queryKey: ['campuses', tenantId] });
                              });
                          }}
                          data-testid={`campus-delete-${c.id}`}>
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      )}
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-1 mt-2">
                    <Badge variant="outline" className="text-[10px]">{meta.label}</Badge>
                    {c.share_timetable ? (
                      <Badge variant="outline" className="text-[10px] bg-indigo-50 text-indigo-700">Shared timetable</Badge>
                    ) : (
                      <Badge variant="outline" className="text-[10px] bg-amber-50 text-amber-700">Own timetable</Badge>
                    )}
                    {c.share_academic_year ? (
                      <Badge variant="outline" className="text-[10px] bg-indigo-50 text-indigo-700">Shared AY</Badge>
                    ) : (
                      <Badge variant="outline" className="text-[10px] bg-amber-50 text-amber-700">Own AY</Badge>
                    )}
                    {!c.is_active && <Badge variant="outline" className="text-[10px] bg-rose-50 text-rose-700">Inactive</Badge>}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>

      {/* Campus dialog */}
      <Dialog open={!!dialog} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent className="sm:max-w-lg" data-testid="campus-dialog">
          <DialogHeader>
            <DialogTitle>{dialog === 'new' ? 'New campus' : `Edit ${form.name}`}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2"><Label>Name</Label>
              <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} data-testid="campus-form-name" />
            </div>
            <div><Label>Code</Label>
              <Input value={form.code || ''} onChange={e => setForm({ ...form, code: e.target.value })} placeholder="MAIN / BYO / ONL" />
            </div>
            <div><Label>Type</Label>
              <Select value={form.type} onValueChange={v => setForm({ ...form, type: v })}>
                <SelectTrigger data-testid="campus-form-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.keys(TYPE_META).map(t => <SelectItem key={t} value={t}>{TYPE_META[t].label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label>City</Label>
              <Input value={form.city || ''} onChange={e => setForm({ ...form, city: e.target.value })} />
            </div>
            <div><Label>Country</Label>
              <Input value={form.country || ''} onChange={e => setForm({ ...form, country: e.target.value })} />
            </div>
            <div><Label>Base Currency</Label>
              <Input
                value={form.currency || ''}
                onChange={e => setForm({ ...form, currency: e.target.value.toUpperCase().slice(0, 4) })}
                placeholder="e.g. GBP — leave empty to inherit school default"
                data-testid="campus-form-currency"
                maxLength={4}
              />
              <p className="text-[11px] text-slate-400 mt-1">Overrides the school's default currency for this campus only. Leave blank to inherit.</p>
            </div>
            <div className="col-span-2"><Label>Address</Label>
              <Input value={form.address || ''} onChange={e => setForm({ ...form, address: e.target.value })} />
            </div>
            <div className="col-span-2 space-y-2 pt-2 border-t">
              <label className="flex items-center gap-2 text-sm">
                <Switch checked={form.share_timetable} onCheckedChange={v => setForm({ ...form, share_timetable: v })} data-testid="campus-form-share-tt" />
                Share timetable with other campuses
              </label>
              <label className="flex items-center gap-2 text-sm">
                <Switch checked={form.share_academic_year} onCheckedChange={v => setForm({ ...form, share_academic_year: v })} data-testid="campus-form-share-ay" />
                Share academic year / terms
              </label>
              <label className="flex items-center gap-2 text-sm">
                <Switch checked={form.is_active} onCheckedChange={v => setForm({ ...form, is_active: v })} />
                Active
              </label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog(null)}>Cancel</Button>
            <Button onClick={() => save.mutate()} disabled={save.isPending} data-testid="campus-form-save">
              {save.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Broadcast dialog */}
      <Dialog open={!!broadcast} onOpenChange={(o) => !o && setBroadcast(null)}>
        <DialogContent className="sm:max-w-lg" data-testid="broadcast-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Send className="h-5 w-5 text-indigo-600" /> Broadcast to campuses
            </DialogTitle>
            <DialogDescription>
              Reach one, many, or all campuses via platform (in-app), email, SMS, or WhatsApp.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div><Label>Subject</Label>
              <Input value={bc.subject} onChange={e => setBc({ ...bc, subject: e.target.value })} data-testid="broadcast-subject" />
            </div>
            <div><Label>Message</Label>
              <Textarea value={bc.body} onChange={e => setBc({ ...bc, body: e.target.value })} rows={4} data-testid="broadcast-body" />
            </div>
            <div>
              <Label>Target campuses</Label>
              {campuses.length === 0 ? (
                <p className="text-xs text-slate-500">Tenant-wide (no campuses created)</p>
              ) : (
                <div className="mt-1 space-y-1">
                  <label className="flex items-center gap-2 text-sm">
                    <Checkbox
                      checked={bc.target_campus_ids.length === 0}
                      onCheckedChange={(v) => v && setBc({ ...bc, target_campus_ids: [] })}
                      data-testid="broadcast-target-all"
                    />
                    Entire tenant (all campuses + shared people)
                  </label>
                  {campuses.map(c => (
                    <label key={c.id} className="flex items-center gap-2 text-sm pl-4">
                      <Checkbox
                        checked={bc.target_campus_ids.includes(c.id)}
                        onCheckedChange={(v) => setBc({
                          ...bc,
                          target_campus_ids: v
                            ? Array.from(new Set([...bc.target_campus_ids, c.id]))
                            : bc.target_campus_ids.filter(x => x !== c.id),
                        })}
                        data-testid={`broadcast-target-${c.id}`}
                      />
                      {c.name}
                    </label>
                  ))}
                </div>
              )}
            </div>
            <div>
              <Label>Channels</Label>
              <div className="flex flex-wrap gap-3 mt-1">
                {Object.keys(bc.channels).map(ch => (
                  <label key={ch} className="flex items-center gap-1.5 text-sm capitalize">
                    <Checkbox
                      checked={bc.channels[ch]}
                      onCheckedChange={(v) => setBc({ ...bc, channels: { ...bc.channels, [ch]: !!v } })}
                      data-testid={`broadcast-ch-${ch}`}
                    />
                    {ch}
                  </label>
                ))}
              </div>
            </div>
            <div>
              <Label>Recipient roles</Label>
              <div className="flex flex-wrap gap-3 mt-1">
                {Object.keys(bc.roles).map(r => (
                  <label key={r} className="flex items-center gap-1.5 text-sm capitalize">
                    <Checkbox
                      checked={bc.roles[r]}
                      onCheckedChange={(v) => setBc({ ...bc, roles: { ...bc.roles, [r]: !!v } })}
                      data-testid={`broadcast-role-${r}`}
                    />
                    {r}s
                  </label>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBroadcast(null)}>Cancel</Button>
            <Button onClick={() => doBroadcast.mutate()} disabled={doBroadcast.isPending} data-testid="broadcast-send-btn">
              {doBroadcast.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Send broadcast'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {leadershipCampus && (
        <CampusLeadershipDialog
          tenantId={tenantId}
          campus={leadershipCampus}
          onClose={() => setLeadershipCampus(null)}
        />
      )}
    </Card>
  );
};


// ============== CAMPUS LEADERSHIP DIALOG ==============
const CampusLeadershipDialog = ({ tenantId, campus, onClose }) => {
  const qc = useQueryClient();
  const [role, setRole] = useState('campus_head');
  const [teacherId, setTeacherId] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [department, setDepartment] = useState('');

  const { data: leaders } = useQuery({
    queryKey: ['campus-leadership', tenantId, campus.id],
    queryFn: async () => {
      const r = await fetch(`${API_URL}/api/school/tenants/${tenantId}/campuses/${campus.id}/leadership`, { headers: authHeaders() });
      if (!r.ok) throw new Error('Failed');
      return r.json();
    },
  });
  const { data: teachers = [] } = useQuery({
    queryKey: ['teachers-for-leadership', tenantId],
    queryFn: async () => {
      const r = await fetch(`${API_URL}/api/school/tenants/${tenantId}/teachers`, { headers: authHeaders() });
      return r.ok ? r.json() : [];
    },
  });

  const assign = useMutation({
    mutationFn: async () => {
      const body = { role, teacher_id: teacherId || null, display_name: displayName || null, department: department || null };
      const r = await fetch(`${API_URL}/api/school/tenants/${tenantId}/campuses/${campus.id}/leadership`, {
        method: 'POST', headers: hdrs(), body: JSON.stringify(body),
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.detail || 'Failed');
      return j;
    },
    onSuccess: () => {
      toast.success('Role assigned');
      setTeacherId(''); setDisplayName(''); setDepartment('');
      qc.invalidateQueries({ queryKey: ['campus-leadership', tenantId, campus.id] });
    },
    onError: (e) => toast.error(e.message),
  });
  const unassign = useMutation({
    mutationFn: async (id) => {
      const r = await fetch(`${API_URL}/api/school/tenants/${tenantId}/campuses/${campus.id}/leadership/${id}`, {
        method: 'DELETE', headers: authHeaders(),
      });
      if (!r.ok) throw new Error('Failed');
      return r.json();
    },
    onSuccess: () => {
      toast.success('Removed');
      qc.invalidateQueries({ queryKey: ['campus-leadership', tenantId, campus.id] });
    },
  });

  const catalog = leaders?.roles_catalog || {};
  const rows = leaders?.items || [];

  return (
    <Dialog open={true} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-lg" data-testid="campus-leadership-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UsersIcon className="h-5 w-5 text-indigo-600" /> {campus.name} — Leadership
          </DialogTitle>
          <DialogDescription>Assign campus-specific leaders: head, vice, registrar, bursar, and HODs.</DialogDescription>
        </DialogHeader>

        <div className="space-y-2">
          {rows.length === 0 ? (
            <p className="text-xs text-slate-500">No leaders assigned yet.</p>
          ) : (
            <div className="space-y-1">
              {rows.map((r) => (
                <div key={r.id} className="flex items-center justify-between p-2 bg-slate-50 rounded text-sm" data-testid={`leadership-row-${r.id}`}>
                  <div>
                    <span className="font-medium">{catalog[r.role] || r.role}</span>
                    {r.department && <span className="text-xs text-slate-500"> · {r.department}</span>}
                    <span className="text-xs text-slate-600 ml-2">{r.display_name || '—'}</span>
                  </div>
                  <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-rose-600" onClick={() => unassign.mutate(r.id)} data-testid={`leadership-remove-${r.id}`}>
                    <X className="h-3.5 w-3.5" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="border-t pt-3 space-y-2">
          <Label className="text-xs">Assign new role</Label>
          <Select value={role} onValueChange={setRole}>
            <SelectTrigger data-testid="leadership-role-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {Object.entries(catalog).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
            </SelectContent>
          </Select>
          {role === 'head_of_department' && (
            <Input placeholder="Department (e.g. Maths)" value={department} onChange={(e) => setDepartment(e.target.value)} data-testid="leadership-dept" />
          )}
          <Select value={teacherId || 'none'} onValueChange={(v) => setTeacherId(v === 'none' ? '' : v)}>
            <SelectTrigger data-testid="leadership-teacher-select"><SelectValue placeholder="Pick staff member" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="none">— External / use display name —</SelectItem>
              {teachers.map((t) => <SelectItem key={t.id} value={t.id}>{t.first_name} {t.last_name}</SelectItem>)}
            </SelectContent>
          </Select>
          {!teacherId && (
            <Input placeholder="Display name (if not a staff member)" value={displayName} onChange={(e) => setDisplayName(e.target.value)} data-testid="leadership-display-name" />
          )}
          <Button size="sm" onClick={() => assign.mutate()} disabled={assign.isPending} data-testid="leadership-assign-btn" className="w-full">
            {assign.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Assign role'}
          </Button>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CampusesCard;
